import type { ThemeAIJunkDecision, ThemeAIScreening, ThemeAIScreeningEstimate } from '../types/gotypes'
import type { JunkCheckConfirmDetails } from '../components/theme-ai-tasks/JunkCheckConfirmDialog'
import { themeAIReaderUnit } from './themeAICost'

// Plain words for the junk check (#1891), in one place so the offer, the
// review and the results line say the same thing.

/** Why starting a run or a test waits: the server answers 409 while a check runs. */
export const JUNK_RUNNING_REASON = 'The junk check is still running. This opens when it finishes.'
/** Why a run of nothing is closed: every flagged answer is dropped and no other answer is left. */
export const JUNK_NOTHING_LEFT_REASON = 'Every answer is flagged as junk and set to drop. Nothing is left to code.'

export const JUNK_CODE = '99'
export const JUNK_NAME = 'Junk / no answer'

const CATEGORY_LABELS: Record<string, string> = {
  empty: 'Empty',
  no_letters: 'No letters',
  keyboard_mash: 'Random characters',
  stock_phrase: 'No answer given',
  copied_text: 'Copied text',
  off_topic: 'Off topic',
  other: 'Other',
}

export const junkCategoryLabel = (category: string): string => CATEGORY_LABELS[category] ?? category

/** Categories in the order the review lists them. */
export const JUNK_CATEGORY_ORDER = Object.keys(CATEGORY_LABELS)

/**
 * The check a run uses: the latest one if it completed, else the older
 * completed one the server sends as `applied` while a newer one runs or
 * after it failed or was cancelled. Review, the decision and Keep act on it.
 */
export const effectiveScreening = (latest: ThemeAIScreening | undefined): ThemeAIScreening | undefined =>
  latest?.status === 'completed' ? latest : latest?.applied

/** "Runs still use the check from 20 Oct: 6 junk answers flagged, dropping them." */
export const junkAppliedLine = (applied: ThemeAIScreening): string => {
  const when = new Date(applied.completed_at || applied.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
  return `Runs still use the check from ${when}: ${junkCountLabel(junkFlaggedCount(applied))} flagged, ${junkDecisionWords(applied.decision)}.`
}

/** How many answers the check flagged for the user to decide on: flagged, less those they keep. */
export const junkFlaggedCount = (screening: ThemeAIScreening): number =>
  Math.max(0, screening.flagged_count - screening.kept_count)

/** The decision as a short phrase for a status line. */
export const junkDecisionWords = (decision: ThemeAIJunkDecision | undefined): string => {
  switch (decision) {
    case 'keep': return 'keeping them all'
    case 'drop': return 'dropping them'
    case 'code': return `coding them as ${JUNK_NAME.toLowerCase()}`
    default: return 'no decision yet'
  }
}

export const junkCountLabel = (count: number): string => `${count.toLocaleString()} junk answer${count === 1 ? '' : 's'}`

/** The run line on Results: what the run did with the flagged answers, or nothing to say. */
export const junkRunLine = (decision: ThemeAIJunkDecision | undefined, count: number | undefined): string | undefined => {
  if (!count) return undefined
  if (decision === 'code') return `${count.toLocaleString()} coded as junk`
  if (decision === 'drop') return `${count.toLocaleString()} junk ${count === 1 ? 'answer' : 'answers'} dropped`
  return undefined
}

/** The label for the run estimate's junk figure: code 99 without the model, or left out. */
export const junkEstimateLabel = (decision: ThemeAIJunkDecision | undefined): string =>
  decision === 'code' ? 'Coded as junk, no model' : 'Left out as junk'

/**
 * The check's estimate as the confirmation reads it. A Manager reads pounds;
 * anyone else reads credits whatever the customer's unit (no-£ rule).
 */
export const junkCheckDetails = (estimate: ThemeAIScreeningEstimate, isManager: boolean): JunkCheckConfirmDetails => ({
  responseCount: estimate.response_count,
  ruleFlagged: estimate.rule_flagged,
  modelCandidates: estimate.model_candidates,
  estimatedTokens: estimate.estimated_tokens,
  estimatedCost: estimate.estimated_cost,
  remainingBudget: estimate.remaining_budget ?? undefined,
  moneyUnit: themeAIReaderUnit(isManager, { display_unit: estimate.display_unit, credit_value_gbp: estimate.credit_value_gbp }),
  priceBasis: estimate.units ? `${estimate.units.toLocaleString('en-GB')} × ${estimate.unit_label ?? 'unit'}` : undefined,
})
